import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  type FC, useCallback, useState,
} from 'react';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { TooltipComponent } from '~/_shared/baseComponents/tooltip/tooltip.component';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import {
  itemHeight, itemWidth,
} from '~/_shared/constants/mainMenuUI.constants';
import { useHover } from '~/_shared/hooks/interaction/useHover';
import type { Theme } from '~/_shared/themes/theme.model';
import { useClickOutsideListenerRef } from '~/_shared/utils/hooks/useClickOutsideListenerRef';
import { MainMenuItemInnerMenuComponent } from './mainMenuItemInnerMenu.component';

const MenuLabel = styled.div({
  fontSize: '10px',
  textAlign: 'center',
  padding: '0 4px',
  width: '100%',
  boxSizing: 'border-box',
});

const MainMenuHeading = styled(MenuLabel)({
  position: 'absolute',
  bottom: -20,
  left: 0,
  transition: 'bottom .2s, opacity .2s',
  opacity: 0,
});

const IconWrapper = styled.div({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  bottom: 0,
  transition: 'bottom .2s',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const HighlightedContent = styled.div(({ theme }) => ({
  color: theme.textColors.quaternary,
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 2,

  '&:before': {
    content: '" "',
    position: 'absolute',
    borderRadius: '50%',
    background: theme.contrasts.success,
    width: 48,
    height: 48,
    left: '50%',
    bottom: '50%',
    transform: 'translate(-50%, 50%)',
  },
}));

const HighlightedLabel = styled(MenuLabel)({
  position: 'relative',
  lineHeight: '12px',
});

const itemStyle = ({ isDisabled, isDanger, labelLinesCount, isActive }: {
  isActive: boolean; isDisabled: boolean; isDanger: boolean; labelLinesCount: number;
}) => (theme: Theme) => {
  const enabledColor = isDanger ? theme.textColors.danger : theme.textColors.primary;

  return css({
    position: 'relative',
    flexShrink: 0,
    backgroundColor: isActive ? theme.backgroundColors.highlight : 'inherit',
    borderRadius: 4,
    boxShadow: isActive ? `0 0 0 1px ${theme.borderColors.activeItem} inset` : 'none',
    boxSizing: 'border-box',
    color: isDisabled ? theme.textColors.disabled : enabledColor,
    width: itemWidth,
    overflow: 'hidden',
    height: '100%',
    userSelect: 'none',
    '&:hover': {
      boxShadow: isActive ? `0 0 0 2px ${theme.borderColors.activeItem} inset` : 'none',
      [`${IconWrapper}`]: {
        bottom: labelLinesCount * 12,
      },
      [`${MainMenuHeading}`]: {
        bottom: 7,
        opacity: 1,
      },
    },
  });
};

const wrapperStyle = ({ isDisabled, fontSize }: { isDisabled: boolean; fontSize?: string }) => (theme: Theme) => css({
  backgroundColor: theme.backgroundColors.secondary,
  borderRadius: 4,
  cursor: isDisabled ? 'default' : 'pointer',
  fontSize,
  height: itemHeight,
  display: 'flex',

  '&:hover': {
    backgroundColor: theme.backgroundColors.tertiary,
    [`${HighlightedContent}:before`]: {
      background: theme.iconColors.backgroundSuccessInvertedHover,
    },
    [`${HighlightedContent}`]: {
      color: theme.textColors.quaternary,
    },
  },
});

const iconStyle = css({
  position: 'relative',
  fontSize: '20px',
});

export type SingleItemProps = Readonly<{
  icon: IconProp;
  title: string;
  onClick: () => void;
  isDisabled: boolean;
  fontSize?: string;
  testId?: string;
}>;

export type MainMenuItemProps = Readonly<SingleItemProps & {
  innerMenu?: ReadonlyArray<SingleItemProps>;
  isActive?: boolean;
  isHighlighted?: boolean;
  isDanger?: boolean;
  isLoading?: boolean;
  className?: string;
  tooltip?: string;
}>;

export const MainMenuItemComponent: FC<MainMenuItemProps> = (props) => {
  const [isInnerItemShown, setIsInnerItemShown] = useState(false);
  const { hoverProps, isHovered } = useHover({
    isDisabled: props.isDisabled,
    onHoverStart: props.innerMenu ? () => setIsInnerItemShown(true) : undefined,
    onHoverEnd: props.innerMenu ? () => setIsInnerItemShown(false) : undefined,
  });

  const renderMenu = !!(props.innerMenu && isInnerItemShown);
  const clickOutsideRef = useClickOutsideListenerRef(() => setIsInnerItemShown(false), renderMenu);

  const { onClick } = props;

  const handleInnerItemClick = useCallback((callback: () => void, isDisabled: boolean) => () => {
    if (!isDisabled) {
      callback();
    }
  }, []);

  const handleClick = useCallback(() => {
    if (!props.isDisabled) {
      if (props.innerMenu && !isHovered) {
        setIsInnerItemShown(prev => !prev);
      }
      onClick();
    }
  }, [props.isDisabled, props.innerMenu, isHovered, onClick]);

  return (
    <div
      className={props.className}
      css={wrapperStyle({ isDisabled: props.isDisabled, fontSize: props.fontSize })}
      {...hoverProps}
    >
      <TooltipComponent tooltipContent={props.tooltip}>
        <div
          css={itemStyle({
            isActive: !!props.isActive || isInnerItemShown,
            isDisabled: props.isDisabled,
            isDanger: !!props.isDanger,
            labelLinesCount: Math.ceil(props.title.length / 10),
          })}
          onClick={handleClick}
          ref={clickOutsideRef}
        >
          {props.isLoading && <OverlayLoaderComponent loaderSize={30} />}

          {props.isHighlighted ? (
            <HighlightedContent>
              <FontAwesomeIcon
                css={iconStyle}
                icon={props.icon}
              />
              <HighlightedLabel>{props.title}</HighlightedLabel>
            </HighlightedContent>
          ) : (
            <>
              <IconWrapper>
                <FontAwesomeIcon
                  css={iconStyle}
                  icon={props.icon}
                />
              </IconWrapper>
              <MainMenuHeading>{props.title}</MainMenuHeading>
            </>
          )}
        </div>
      </TooltipComponent>

      {renderMenu && (
        <MainMenuItemInnerMenuComponent
          innerMenu={props.innerMenu}
          onItemClick={handleInnerItemClick}
        />
      )}
    </div>
  );
};
